import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Output,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { UiBottomSheetComponent } from '../../ui-kit/ui-bottom-sheet/ui-bottom-sheet.component';
import { UiButtonComponent } from '../../ui-kit/ui-button/ui-button.component';
import { UiSearchComponent } from '../../ui-kit/ui-search/ui-search.component';

export interface BottomSheetOption {
  value: any;
  viewValue: string;
  disabled: boolean;
  group: string | null;
  image?: string | null;
}

@Component({
  selector: 'mat-select-bottom-sheet-content',
  standalone: true,
  imports: [
    UiBottomSheetComponent,
    UiButtonComponent,
    UiSearchComponent,
    MatIcon,
    TranslatePipe,
    TitleCasePipe,
    FormsModule,
  ],
  templateUrl: './mat-select-bottom-sheet-content.component.html',
  styleUrls: ['./mat-select-bottom-sheet-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatSelectBottomSheetContentComponent {
  /** Configured by the directive — not template inputs. */
  readonly options = signal<BottomSheetOption[]>([]);
  readonly isMultiple = signal(false);
  readonly title = signal('');
  readonly searchEnabled = signal(false);
  readonly compareWith = signal<(a: any, b: any) => boolean>(
    (a, b) => a === b,
  );

  readonly sheetOpen = signal(false);
  readonly localSelection = signal<any[]>([]);
  readonly searchQuery = signal('');

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly applied = new EventEmitter<any>();

  readonly filteredOptions = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const opts = this.options();
    if (!query) return opts;
    return opts.filter((o) =>
      o.viewValue.toLowerCase().includes(query),
    );
  });

  open(value: any): void {
    this.searchQuery.set('');
    if (this.isMultiple()) {
      this.localSelection.set(
        Array.isArray(value) ? [...value] : [],
      );
    } else {
      this.localSelection.set(value != null ? [value] : []);
    }
    this.sheetOpen.set(true);
  }

  close(): void {
    this.sheetOpen.set(false);
    this.searchQuery.set('');
    this.closed.emit();
  }

  onOptionTap(option: BottomSheetOption): void {
    if (option.disabled) return;

    const cmp = this.compareWith();
    if (this.isMultiple()) {
      const current = this.localSelection();
      const idx = current.findIndex((v) => cmp(v, option.value));
      if (idx > -1) {
        this.localSelection.set(current.filter((_, i) => i !== idx));
      } else {
        this.localSelection.set([...current, option.value]);
      }
    } else {
      this.localSelection.set([option.value]);
      this.apply();
    }
  }

  apply(): void {
    const val = this.isMultiple()
      ? this.localSelection()
      : this.localSelection()[0] ?? null;
    this.applied.emit(val);
    this.close();
  }

  isSelected(value: any): boolean {
    const cmp = this.compareWith();
    return this.localSelection().some((v) => cmp(v, value));
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.searchQuery.set('');
  }
}
